import React from 'react';

// FAQ Schema Component
interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  faqs: FAQItem[];
}

export const FAQSchema: React.FC<FAQSchemaProps> = ({ faqs }) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }))
  };

  return (
    <script type="application/ld+json">
      {JSON.stringify(structuredData)}
    </script>
  );
};

// How-To Schema Component
interface HowToStep {
  name: string;
  text: string;
  image?: string;
}

interface HowToSchemaProps {
  name: string;
  description: string;
  steps: HowToStep[];
  totalTime?: string;
  estimatedCost?: {
    currency: string;
    value: string;
  };
}

export const HowToSchema: React.FC<HowToSchemaProps> = ({
  name,
  description,
  steps,
  totalTime,
  estimatedCost
}) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': name,
    'description': description,
    'step': steps.map((step, index) => ({
      '@type': 'HowToStep',
      'position': index + 1,
      'name': step.name,
      'text': step.text,
      ...(step.image && { 'image': step.image })
    })),
    ...(totalTime && { 'totalTime': totalTime }),
    ...(estimatedCost && { 'estimatedCost': estimatedCost })
  };

  return (
    <script type="application/ld+json">
      {JSON.stringify(structuredData)}
    </script>
  );
};

// Product Schema Component
interface ProductSchemaProps {
  name: string;
  description: string;
  image: string;
  offers: {
    price: string;
    currency: string;
    availability: string;
    validFrom?: string;
  };
  aggregateRating?: {
    ratingValue: string;
    reviewCount: string;
  };
  brand?: string;
}

export const ProductSchema: React.FC<ProductSchemaProps> = ({
  name,
  description,
  image,
  offers,
  aggregateRating,
  brand
}) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': name,
    'description': description,
    'image': image,
    'offers': {
      '@type': 'Offer',
      'price': offers.price,
      'priceCurrency': offers.currency,
      'availability': offers.availability,
      ...(offers.validFrom && { 'validFrom': offers.validFrom })
    },
    ...(aggregateRating && { 'aggregateRating': aggregateRating }),
    ...(brand && { 'brand': { '@type': 'Brand', 'name': brand } })
  };

  return (
    <script type="application/ld+json">
      {JSON.stringify(structuredData)}
    </script>
  );
};

// Article Schema Component
interface ArticleSchemaProps {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified?: string;
  author: {
    name: string;
    url?: string;
  };
  publisher: {
    name: string;
    logo: string;
  };
  mainEntityOfPage: string;
  keywords?: string[];
}

export const ArticleSchema: React.FC<ArticleSchemaProps> = ({
  headline,
  description,
  image,
  datePublished,
  dateModified,
  author,
  publisher,
  mainEntityOfPage,
  keywords
}) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': headline,
    'description': description,
    'image': image,
    'datePublished': datePublished,
    ...(dateModified && { 'dateModified': dateModified }),
    'author': {
      '@type': 'Person',
      'name': author.name,
      ...(author.url && { 'url': author.url })
    },
    'publisher': {
      '@type': 'Organization',
      'name': publisher.name,
      'logo': {
        '@type': 'ImageObject',
        'url': publisher.logo
      }
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': mainEntityOfPage
    },
    ...(keywords && { 'keywords': keywords.join(', ') })
  };

  return (
    <script type="application/ld+json">
      {JSON.stringify(structuredData)}
    </script>
  );
};

// Breadcrumb Schema Component
interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export const BreadcrumbSchema: React.FC<BreadcrumbSchemaProps> = ({ items }) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.url
    }))
  };

  return (
    <script type="application/ld+json">
      {JSON.stringify(structuredData)}
    </script>
  );
};

// Video Schema Component
interface VideoSchemaProps {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
  contentUrl?: string;
  embedUrl?: string;
}

export const VideoSchema: React.FC<VideoSchemaProps> = ({
  name,
  description,
  thumbnailUrl,
  uploadDate,
  duration,
  contentUrl,
  embedUrl
}) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    'name': name,
    'description': description,
    'thumbnailUrl': thumbnailUrl,
    'uploadDate': uploadDate,
    ...(duration && { 'duration': duration }),
    ...(contentUrl && { 'contentUrl': contentUrl }),
    ...(embedUrl && { 'embedUrl': embedUrl })
  };

  return (
    <script type="application/ld+json">
      {JSON.stringify(structuredData)}
    </script>
  );
};
